// components/common/PaymentModal.jsx
import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Initialize Stripe (using mock key for testing)
const stripePromise = loadStripe('pk_test_mock_key_for_development'); // Mock test publishable key

const PaymentForm = ({ course, onSuccess, onCancel, loading: parentLoading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    country: 'US'
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create payment intent
      const response = await fetch('/api/payments/create-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
          // Temporarily removed authorization for testing
          // 'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          courseId: course.id,
          billingDetails
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to create payment intent');
      }

      // Handle free course enrollment (0 EUR/USD)
      if (result.amount === 0) {
        onSuccess({
          success: true,
          enrollment: result.enrollment,
          message: 'Free enrollment completed successfully'
        });
        return;
      }

      // Handle mock payment for testing
      if (result.isMock) {
        // Simulate a successful payment without Stripe
        setTimeout(() => {
          onSuccess({
            success: true,
            payment: result,
            message: 'Mock payment completed successfully - Course enrollment activated!'
          });
        }, 2000); // Simulate processing time
        return;
      }

      // Real Stripe payment processing
      if (!stripe || !elements) {
        throw new Error('Stripe not loaded properly');
      }

      // Confirm payment with Stripe for paid courses
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        result.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: billingDetails.name,
              email: billingDetails.email,
              address: {
                country: billingDetails.country
              }
            }
          }
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirm payment on our backend
        const confirmResponse = await fetch('/api/payments/confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id
          })
        });

        const confirmResult = await confirmResponse.json();

        if (confirmResult.success) {
          onSuccess(confirmResult);
        } else {
          throw new Error(confirmResult.message);
        }
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        backgroundColor: '#374151',
        '::placeholder': {
          color: '#9ca3af'
        }
      },
      invalid: {
        color: '#ef4444'
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ 
          color: 'white', 
          fontSize: '1.25rem', 
          marginBottom: '16px',
          fontWeight: '600'
        }}>
          Payment Details
        </h3>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            color: '#d1d5db', 
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Full Name *
          </label>
          <input
            type="text"
            value={billingDetails.name}
            onChange={(e) => setBillingDetails({ ...billingDetails, name: e.target.value })}
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #4b5563',
              backgroundColor: '#374151',
              color: 'white',
              fontSize: '16px',
              outline: 'none'
            }}
            placeholder="Enter your full name"
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            color: '#d1d5db', 
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Email Address *
          </label>
          <input
            type="email"
            value={billingDetails.email}
            onChange={(e) => setBillingDetails({ ...billingDetails, email: e.target.value })}
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #4b5563',
              backgroundColor: '#374151',
              color: 'white',
              fontSize: '16px',
              outline: 'none'
            }}
            placeholder="Enter your email"
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            color: '#d1d5db', 
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Country *
          </label>
          <select
            value={billingDetails.country}
            onChange={(e) => setBillingDetails({ ...billingDetails, country: e.target.value })}
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #4b5563',
              backgroundColor: '#374151',
              color: 'white',
              fontSize: '16px',
              outline: 'none'
            }}
          >
            <option value="US">United States</option>
            <option value="UK">United Kingdom</option>
            <option value="EU">European Union</option>
            <option value="NG">Nigeria</option>
            <option value="FR">France</option>
            <option value="DE">Germany</option>
            <option value="ES">Spain</option>
            <option value="IT">Italy</option>
          </select>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            color: '#d1d5db', 
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Card Details *
          </label>
          <div style={{
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #4b5563',
            backgroundColor: '#374151'
          }}>
            <CardElement options={cardElementOptions} />
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end'
      }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading || parentLoading}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            border: '1px solid #4b5563',
            backgroundColor: 'transparent',
            color: '#d1d5db',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading || parentLoading}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: loading ? '#94a3b8' : '#06b6d4',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {loading ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid transparent',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Processing...
            </>
          ) : (
            <>
              🔒 {course.price === 0 ? 'Complete Free Enrollment' : `Pay $${course.price}`}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const PaymentModal = ({ isOpen, onClose, course, onSuccess }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: '16px',
        maxWidth: '500px',
        width: '100%',
        padding: '32px',
        position: 'relative',
        border: '1px solid #374151',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            backgroundColor: '#374151',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            fontSize: '20px',
            fontWeight: 'bold'
          }}
        >
          ×
        </button>

        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '8px'
          }}>
            Complete Your Enrollment
          </h2>
          <p style={{
            color: '#9ca3af',
            fontSize: '16px',
            marginBottom: '16px'
          }}>
            You're enrolling in: <strong style={{ color: '#06b6d4' }}>{course.title}</strong>
          </p>
          
          <div style={{
            backgroundColor: '#111827',
            border: '1px solid #374151',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <span style={{ color: '#d1d5db' }}>Course Price:</span>
              <span style={{ color: 'white', fontWeight: '600' }}>
                {course.price === 0 ? '0 €' : `$${course.price}`}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <span style={{ color: '#d1d5db' }}>Tax:</span>
              <span style={{ color: 'white' }}>
                {course.price === 0 ? '0 €' : 'Calculated at checkout'}
              </span>
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid #374151', margin: '12px 0' }} />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ color: '#d1d5db', fontSize: '18px', fontWeight: '600' }}>Total:</span>
              <span style={{ color: '#06b6d4', fontSize: '18px', fontWeight: 'bold' }}>
                {course.price === 0 ? '0 €' : `$${course.price}+`}
              </span>
            </div>
          </div>
        </div>

        <Elements stripe={stripePromise}>
          <PaymentForm
            course={course}
            onSuccess={onSuccess}
            onCancel={onClose}
          />
        </Elements>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PaymentModal;
